import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { KTSVG } from '../../_metronic/helpers';
import { useDeleteImageMutation, useUploadImageMutation } from '../../reducers/fileApiSlice';
import { API_ROOT } from '../../reducers/services/api';

export type MediaResponse = MediaItem & Record<string, unknown>;

type Props = {
	onUpload?: (media: MediaResponse[]) => void;
	onRemove?: (media: MediaItem) => void;
	disabled?: boolean;
	files: MediaItem[];
	limit?: number;
	allowedTypes?: string[];
	multiple?: boolean;
};

export type MediaItem = {
	_id: string;
	title: string;
	url_sm: string;
	url?: string;
};

const File: React.FC<Props> = ({
	onUpload,
	onRemove,
	allowedTypes = ['image/jpg, image/jpeg', 'application/pdf'],
	files = [],
	limit = 10,
	disabled = false,
	multiple = false,
}) => {
	const [uploadImage] = useUploadImageMutation();
	const [deleteImage] = useDeleteImageMutation();
	const [media, setMedia] = useState<MediaItem[]>(files);
	const [isUploading, setIsUploading] = useState(false);
	const id = useMemo(() => 'file' + new Date().getTime(), []);

	const getFileName = (file) => {
		if (file.title) {
			return file.title;
		}
		if (file.name) {
			return file.name;
		}
		if (file.url) {
			return file.url;
		}
		return 'Unnamed File';
	};

	const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const fileInput = e.target;
		const files = fileInput.files;

		if (files && files.length > 0 && !isUploading) {
			try {
				setIsUploading(true);
				const formData = new FormData();
				Array.from(files).forEach((file) => {
					formData.append('image', file);
				});

				const response = await uploadImage(formData);
				if ('data' in response) {
					const newMedia: MediaItem = {
						_id: response.data.media._id,
						title: response.data.media.title,
						url_sm: response.data.media.url_sm,
					};
					onUpload && onUpload(response.data.media); //MediaResponse
				}
			} catch (error) {
				console.error('File upload failed:', error);
			} finally {
				setIsUploading(false);
				// Reset the input value to ensure onChange triggers for the same file
				fileInput.value = '';
			}
		}
	};

	return (
		<div>
			<div className="d-flex align-items-center">
				<div className="uploader-container fs-5 fw-bold w-100">
					<div className="uploader-wrapper p-4">
						<input
							type="file"
							id={id}
							className="custom-checkbox w-100 h-100"
							accept={allowedTypes.join(',')}
							onChange={handleChange}
							disabled={disabled || isUploading}
							multiple={multiple}
						/>
						<label htmlFor={id} className="uploader">
							{!multiple && files && files.length > 0 ? (
								files.map((m) => (
									<div key={m._id} className="d-inline-flex align-items-center me-3 mb-2">
										<img
											src={API_ROOT + '/' + (m.url_sm ?? m.url)}
											alt={m.title}
											className="me-2"
											onClick={() => {
												onUpload && onUpload(media.filter(({ _id }) => _id !== m._id));
											}}
											style={{ width: '40px', height: '40px', objectFit: 'cover' }}
										/>
									</div>
								))
							) : (
								<KTSVG className="svg-icon-4x svg-icon-primary" path="/media/icons/duotune/files/fil009.svg" />
							)}

							<div className="uploader-text">
								<span className="fs-bold">
									<FormattedMessage id="FILES.UPLOAD" />
								</span>
								<span className="fs-7 text-primary">
									<FormattedMessage id="FILES.DRAG.DROP" />
								</span>
							</div>
						</label>
					</div>
				</div>
				{!multiple && files && files.length > 0 && files.length <= 1 ? (
					<div
						className="ms-3 btn btn-icon btn-light-danger"
						style={{ width: '40px', height: '40px' }}
						onClick={async (e) => {
							// handleDeletion(`translations[${fileLang}].menu`);
							files?.[0] && files?.[0]._id ? await deleteImage({ id: files[0]._id }) : setMedia([]);
							onRemove && onRemove(files[0]);
						}}
					>
						<KTSVG className="svg-icon-2" path="/media/icons/duotune/general/gen040.svg" />
					</div>
				) : null}
			</div>
			{multiple && files && files.length > 0 && (
				<div className="mt-5">
					{files.map((file) => (
						<div key={file._id} className="d-flex align-items-center mb-3">
							<div className="p-3 bg-light rounded-3 w-100 d-inline-flex align-items-center">
								<img
									className="object-fit-cover me-5 rounded-3"
									width={50}
									height={50}
									src={API_ROOT + '/' + (file.url_sm ?? file.url)}
									alt={file.title}
								/>
								<div className="fs-7">
									<span className="fw-bold">{getFileName(file)}</span>
									<br />
									<span className="text-primary">{file?.url && file?.url.split('/').pop()}</span>
								</div>
							</div>
							<div
								className="ms-3 btn btn-icon btn-light-danger"
								style={{ width: '40px', height: '40px' }}
								onClick={async (e) => {
									file._id ? await deleteImage({ id: file._id }) : setMedia([]);
									onRemove && onRemove(file);
								}}
							>
								<KTSVG className="svg-icon-2" path="/media/icons/duotune/general/gen040.svg" />
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default File;
